/**
 * ApprovalCard — renders a Human-in-the-Loop approval prompt inline in the
 * chat stream. When an agent calls a tool with `requires_confirmation=True`,
 * the run pauses and this card appears showing the tool name and its
 * arguments. The user can approve or reject the action.
 *
 * On approve/reject, the parent calls `continueRun()` which POSTs to
 * `/api/agents/{id}/runs/{run_id}/continue` with the resolved tool
 * executions, resuming the SSE stream.
 *
 * This is a thin wrapper around the AI Elements `Confirmation` component
 * (from the ai-elements registry) that adapts our agno-specific HITL
 * data model (RunRequirementInfo) to the component's context-driven API.
 */

import { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import type {
  RunRequirementInfo,
  HitlExplanation,
  HumanSecretInputDescriptor,
} from "@/lib/stream-parser";
import {
  Confirmation,
  ConfirmationAccepted,
  ConfirmationRejected,
} from "@/components/ai-elements/confirmation";
import { Badge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

const APPROVAL_EXECUTION_TOOL_NAME = "exec_skill_script_with_approval";

/**
 * A parsed command-line slot in an approval `args` list.
 * - `{ kind: "flag", flag, value }` — a `-x/--flag` with its following value.
 * - `{ kind: "positional", position, value }` — a bare token (e.g. a leading
 *   subcommand such as `prepare`, or a lone flag with no value).
 *
 * We intentionally do NOT require the list to be even-length or to start with
 * a flag: real skill scripts commonly begin with a positional subcommand
 * (`prepare`, `run`, `extract` …) followed by flag/value pairs, and a trailing
 * lone flag is valid too. Anything that cannot be paired falls back to the
 * indexed `# | Argument` table instead.
 */
type ParsedArgument =
  | { kind: "flag"; flag: string; value: string }
  | { kind: "positional"; position: number; value: string };

function isFlagLike(argument: unknown): argument is string {
  return typeof argument === "string" && argument.startsWith("-");
}

/** Convert a raw `unknown[]` into typed flag/value rows, or null if no flag
 *  was found at all (so we can fall back to the indexed rendering). */
function parseFlagValueArguments(args: unknown[]): ParsedArgument[] | null {
  const rows: ParsedArgument[] = [];
  let position = 0;

  for (let index = 0; index < args.length; index += 1) {
    const token = args[index];

    if (isFlagLike(token)) {
      // Flag in position `index`, value is the next element (if any).
      const rawValue = index + 1 < args.length ? args[index + 1] : undefined;
      rows.push({
        kind: "flag",
        flag: token,
        value: rawValue === undefined
          ? ""
          : typeof rawValue === "string" || typeof rawValue === "number"
            ? String(rawValue)
            : JSON.stringify(rawValue) ?? "undefined",
      });
      index += 1; // consume the value
    } else {
      // Positional token (subcommand, operand, or anything not flag-like).
      rows.push({
        kind: "positional",
        position,
        value: typeof token === "string" || typeof token === "number"
          ? String(token)
          : JSON.stringify(token) ?? "undefined",
      });
      position += 1;
    }
  }

  // If nothing was flag-like, don't attempt a Flag|Value table at all — the
  // caller keeps its indexed `# | Argument` rendering (e.g. `["prepare",
  // "out.json"]` with no `-o` would be misleading as Flag|Value rows).
  return rows.some((row) => row.kind === "flag") ? rows : null;
}

/** Risk-level → subtle shadcn badge presentation. */
const RISK_CONFIG: Record<
  HitlExplanation["risk_level"],
  { badgeVariant: "outline" | "secondary"; badgeClass: string; labelKey: string }
> = {
  critical: {
    badgeVariant: "outline",
    badgeClass: "border-destructive/50 bg-destructive/5 text-destructive font-normal",
    labelKey: "approval.risk.critical",
  },
  high: {
    badgeVariant: "outline",
    badgeClass: "border-orange-500/50 bg-orange-50 text-orange-700 font-normal dark:border-orange-400/50 dark:bg-orange-950/20 dark:text-orange-400",
    labelKey: "approval.risk.high",
  },
  medium: {
    badgeVariant: "outline",
    badgeClass: "border-amber-500 bg-amber-50 text-amber-600 font-normal dark:border-amber-500 dark:bg-amber-950/20 dark:text-amber-400",
    labelKey: "approval.risk.medium",
  },
  low: {
    badgeVariant: "secondary",
    badgeClass: "font-normal",
    labelKey: "approval.risk.low",
  },
};

export interface EphemeralSecretInput {
  requirement_id: string;
  value: string;
}

interface ApprovalCardProps {
  runId: string;
  requirements: RunRequirementInfo[];
  agentId: string;
  sessionId: string;
  onContinue: (
    runId: string,
    tools: string,
    secretInputs?: EphemeralSecretInput,
    onAccepted?: () => void,
  ) => Promise<void>;
  /** When true, the card renders as a static "was approved/rejected"
   *  confirmation instead of an interactive approval prompt. This is the
   *  state restored from the DB marker row after session navigation. */
  resolved?: boolean;
  recoveryError?: string;
  fetchWithAuth?: (url: string, init: RequestInit) => Promise<Response>;
  /** Only the session steward may answer an unresolved approval request. */
  canResolve?: boolean;
}

export function ApprovalCard({
  runId,
  requirements,
  agentId,
  sessionId,
  onContinue,
  resolved = false,
  recoveryError,
  fetchWithAuth,
  canResolve = true,
}: ApprovalCardProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"pending" | "approved" | "rejected" | "loading" | "running" | "failed">("pending");
  const [pendingDecision, setPendingDecision] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [staleRun, setStaleRun] = useState(false);
  const [checkingRun, setCheckingRun] = useState(() => !resolved && !!fetchWithAuth && !!sessionId && !!runId);
  useEffect(() => {
    if (resolved || !fetchWithAuth || !sessionId || !runId) return;
    let active = true;
    setStaleRun(false);
    setCheckingRun(true);
    void fetchWithAuth(`/api/sessions/${encodeURIComponent(sessionId)}/runs`, { headers: {} })
      .then(async (response) => {
        if (!response.ok) return;
        const body: unknown = await response.json();
        if (!body || typeof body !== "object" || !("runs" in body) || !Array.isArray(body.runs)) return;
        const run = body.runs.find((entry: unknown) =>
          !!entry && typeof entry === "object" && "run_id" in entry && entry.run_id === runId);
        if (active && run && typeof run.status === "string") setStaleRun(run.status !== "paused");
      })
      .catch(() => { /* Older or unavailable servers retain the actionable card. */ })
      .finally(() => { if (active) setCheckingRun(false); });
    return () => { active = false; };
  }, [resolved, fetchWithAuth, sessionId, runId]);
  const [startedAt, setStartedAt] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (status !== "running") return;
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [status, startedAt]);
  // Derive the persisted result during render so a marker restored from the
  // DB is never briefly shown as an interactive prompt before this effect.
  const persistedStatus = resolved
    ? requirements[0]?.tool_execution?.confirmed === false
      ? "rejected"
      : "approved"
    : undefined;
  // Passwords intentionally never enter React state or message metadata.
  // Keeping refs to the DOM nodes also avoids creating an extra JS copy while
  // the operator is typing.
  const secretInputRefs = useRef(new Map<string, HTMLInputElement>());
  const secretInputRefCallbacks = useRef(
    new Map<string, (node: HTMLInputElement | null) => void>(),
  );

  const getSecretInputRef = useCallback((requirementId: string) => {
    const existing = secretInputRefCallbacks.current.get(requirementId);
    if (existing) return existing;

    // React calls a replaced callback ref with null even when it retains the
    // DOM node. Keep this callback stable per requirement so only a real
    // detach clears the password.
    const callback = (node: HTMLInputElement | null) => {
      if (node) {
        secretInputRefs.current.set(requirementId, node);
        return;
      }

      const previous = secretInputRefs.current.get(requirementId);
      if (previous) {
        // Zero the DOM value at the exact moment React detaches the node. The
        // map may already have been drained by the unmount cleanup effect, so
        // clearing here is the only ordering-safe guarantee.
        previous.value = "";
        secretInputRefs.current.delete(requirementId);
      }
    };
    secretInputRefCallbacks.current.set(requirementId, callback);
    return callback;
  }, []);

  const clearSecretInputs = () => {
    for (const input of secretInputRefs.current.values()) {
      input.value = "";
    }
  };

  useEffect(() => {
    return () => {
      // Covers unmount/navigation/abort paths.  The browser owns the input
      // value until this cleanup runs and it is never persisted by the card.
      clearSecretInputs();
      secretInputRefs.current.clear();
      secretInputRefCallbacks.current.clear();
    };
  }, []);

  // Keep local state in sync with a DB-restored marker as the requirements
  // arrive or are reconciled from the resumed stream.
  useEffect(() => {
    if (persistedStatus && status !== "loading" && status !== "running" && status !== "failed" && !error) setStatus(persistedStatus);
  }, [persistedStatus, status, error]);

  const handleApprove = async () => {
    setPendingDecision("approve");
    setStatus("loading");
    setError(null);
    let secretInputs: EphemeralSecretInput | undefined;
    let validationError = false;
    try {
      const secretRequirements = requirements.filter(
        (req) => req.human_secret_input?.required,
      );
      if (secretRequirements.length > 1) {
        validationError = true;
        throw new Error(t("approval.multipleSecretInputs"));
      }
      if (secretRequirements.length === 1) {
        const requirement = secretRequirements[0];
        const descriptor = requirement.human_secret_input as HumanSecretInputDescriptor;
        const input = secretInputRefs.current.get(descriptor.requirement_id);
        const value = input?.value ?? "";
        if (!value) {
          validationError = true;
          throw new Error(t("approval.secretRequired", { label: descriptor.label }));
        }
        secretInputs = {
          requirement_id: descriptor.requirement_id,
          value,
        };
      }
      const tools = requirements.map((req) => ({
        ...req.tool_execution,
        confirmed: true,
        requires_confirmation: true,
      }));
      await onContinue(runId, JSON.stringify(tools), secretInputs, () => {
        setStartedAt(Date.now());
        setElapsed(0);
        setStatus("running");
      });
      setStatus("approved");
    } catch (err) {
      const message = err instanceof Error ? err.message : t("approval.continueFailed");
      toast.error(message);
      setError(message);
      setStatus(validationError || (err as { retryable?: boolean })?.retryable ? "pending" : "failed");
    } finally {
      // Clear after both successful handoff and failed requests; a retry must
      // be an explicit new entry by the human.
      secretInputs = undefined;
      clearSecretInputs();
      setPendingDecision(null);
    }
  };

  const handleReject = async () => {
    setPendingDecision("reject");
    setStatus("loading");
    setError(null);
    clearSecretInputs();
    try {
      const tools = requirements.map((req) => ({
        ...req.tool_execution,
        confirmed: false,
        requires_confirmation: true,
      }));
      await onContinue(runId, JSON.stringify(tools), undefined, () => {
        setStartedAt(Date.now());
        setElapsed(0);
        setStatus("running");
      });
      setStatus("rejected");
    } catch (err) {
      const message = err instanceof Error ? err.message : t("approval.continueFailed");
      toast.error(message);
      setError(message);
      setStatus((err as { retryable?: boolean })?.retryable ? "pending" : "failed");
    } finally {
      clearSecretInputs();
      setPendingDecision(null);
    }
  };

  const displayStatus = status === "loading" || status === "running" || status === "failed" || error
    ? status : persistedStatus ?? status;

  // Map resolved results to the Confirmation component's static state model.
  const confirmationState =
    displayStatus === "approved" || displayStatus === "rejected"
      ? "approval-responded"
      : "approval-requested";

  const approval = { id: runId, approved: displayStatus === "approved" ? true : displayStatus === "rejected" ? false : undefined } as
    | { id: string; approved: true }
    | { id: string; approved: false }
    | { id: string; approved?: never };

  const primaryRequirement = requirements[0];
  const primaryToolName = primaryRequirement?.tool_execution?.tool_name ?? t("approval.unknownTool");
  const primaryMemberName = primaryRequirement?.member_agent_name;
  const headerBadges = requirements.flatMap((requirement, index) => {
    const explanation = requirement.explanation;
    if (!explanation) return [];

    const risk = RISK_CONFIG[explanation.risk_level];
    return [
      <Badge
        key={`risk-${index}`}
        variant={risk.badgeVariant}
        className={risk.badgeClass}
      >
        {t(risk.labelKey)}
      </Badge>,
      ...(explanation.is_destructive
        ? [
            <Badge
              key={`destructive-${index}`}
              variant="outline"
              className="border-destructive/50 bg-destructive/5 text-destructive font-normal"
            >
              {t("approval.destructive")}
            </Badge>,
          ]
        : []),
      ...(explanation.is_modifying && !explanation.is_destructive
        ? [
            <Badge
              key={`modifies-${index}`}
              variant="secondary"
              className="font-normal"
            >
              {t("approval.modifiesState")}
            </Badge>,
          ]
        : []),
    ];
  });

  // The interactive request uses a Card directly so its header, body, and
  // footer provide the visual hierarchy. Resolved results retain the
  // Confirmation wrapper below, which is what lets marker rows remain static
  // while the resumed stream continues.
  if (displayStatus !== "approved" && displayStatus !== "rejected") {
    return (
      <Card className="my-3 overflow-hidden border-l-4 border-l-amber-500">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-2">
          <div className="min-w-0 space-y-1">
            <CardTitle>{t("approval.title")}</CardTitle>
            <CardDescription>
              {primaryMemberName
                ? t("approval.memberWantsToCall", { name: primaryMemberName })
                : t("approval.agentWantsToCall")}{" "}
              <code className="break-all rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {primaryToolName}
              </code>
            </CardDescription>
          </div>
          {headerBadges.length > 0 && (
            <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
              {headerBadges}
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {requirements.map((req, i) => {
            const toolName = req.tool_execution?.tool_name ?? t("approval.unknownTool");
            const toolArgs = req.tool_execution?.tool_args ?? {};
            const memberName = req.member_agent_name;
            const hasArgs = Object.keys(toolArgs).length > 0;
            const isApprovalExecutionTool = toolName === APPROVAL_EXECUTION_TOOL_NAME;

            return (
              <div
                key={req.id ?? i}
                className={cn(
                  "min-w-0 space-y-4",
                  i > 0 && "border-t border-border pt-4",
                )}
              >
                {i > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {memberName
                      ? t("approval.memberWantsToCall", { name: memberName })
                      : t("approval.agentWantsToCall")}{" "}
                    <code className="break-all rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                      {toolName}
                    </code>
                  </p>
                )}

                {/* HITL explanation — LLM-generated risk assessment.
                    It stays close to the action without becoming a second
                    visually competing container. */}
                {req.explanation && (
                  <>
                    <p className="break-words text-sm text-muted-foreground">
                      {req.explanation.summary}
                    </p>
                    {req.explanation.warnings.length > 0 && (
                      <ul className="space-y-2">
                        {req.explanation.warnings.map((warning, wi) => (
                          <li
                            key={wi}
                            className="flex min-w-0 gap-2 text-sm text-muted-foreground"
                          >
                            <AlertTriangle
                              aria-hidden="true"
                              className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400"
                            />
                            <span className="break-words">{warning}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}

                {req.human_secret_input?.required && (
                  <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                    <label
                      className="text-sm font-medium text-foreground"
                      htmlFor={`hitl-secret-${req.human_secret_input.requirement_id}`}
                    >
                      {req.human_secret_input.label}
                    </label>
                    {req.human_secret_input.help && (
                      <p className="text-xs text-muted-foreground">
                        {req.human_secret_input.help}
                      </p>
                    )}
                    <input
                      id={`hitl-secret-${req.human_secret_input.requirement_id}`}
                      data-testid="hitl-secret-input"
                      type="password"
                      autoComplete="new-password"
                      maxLength={req.human_secret_input.max_length}
                      ref={getSecretInputRef(req.human_secret_input.requirement_id)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      aria-label={req.human_secret_input.label}
                    />
                  </div>
                )}

                {/* Parameter details — collapsible so the operator is
                    encouraged to read the explanation first and only
                    drill into raw args if they want to verify. */}
                {hasArgs && (
                  <Collapsible>
                    <CollapsibleTrigger>
                      <span className="text-muted-foreground">{t("approval.parameters")}</span>
                      <ChevronDown className="chevron h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="overflow-x-auto overflow-y-auto rounded-md border border-border bg-background">
                        <table className="w-full table-fixed divide-y divide-border">
                          <thead className="bg-muted/80">
                            <tr className="border-border">
                              <th className="w-1/3 whitespace-nowrap px-4 py-2 text-left font-semibold text-sm">
                                {t("approval.parameter")}
                              </th>
                              <th className="w-2/3 whitespace-nowrap px-4 py-2 text-left font-semibold text-sm">
                                {t("approval.value")}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {Object.entries(toolArgs).map(([key, value]) => (
                              <tr key={key} className="border-border">
                                <td className="break-all px-4 py-2 text-sm">
                                  {key}
                                </td>
                                <td className="break-all px-4 py-2 text-sm">
                                  {isApprovalExecutionTool && key === "args" && Array.isArray(value) ? (
                                    value.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">{t("approval.noArguments")}</p>
                                    ) : (() => {
                                      const parsedArgs = parseFlagValueArguments(value);

                                      return parsedArgs ? (
                                        <div className="overflow-x-auto overflow-y-auto rounded-md border border-border bg-background">
                                          <table className="w-full table-fixed divide-y divide-border" aria-label={t("approval.flagArguments")}>
                                            <thead className="bg-muted/80">
                                              <tr className="border-border">
                                                <th className="w-1/3 whitespace-nowrap px-4 py-2 text-left font-semibold text-sm">
                                                  {t("approval.flag")}
                                                </th>
                                                <th className="w-2/3 whitespace-nowrap px-4 py-2 text-left font-semibold text-sm">
                                                  {t("approval.value")}
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border">
                                              {parsedArgs.map((row, index) => (
                                                <tr key={index} className="border-border">
                                                  <td className="break-all px-4 py-2 font-mono text-sm">
                                                    {row.kind === "flag"
                                                      ? row.flag
                                                      : `#${row.position}`}
                                                  </td>
                                                  <td className="break-all px-4 py-2 font-mono text-sm">
                                                    {row.value}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className="overflow-x-auto overflow-y-auto rounded-md border border-border bg-background">
                                          <table className="w-full table-fixed divide-y divide-border" aria-label={t("approval.arguments")}>
                                            <thead className="bg-muted/80">
                                              <tr className="border-border">
                                                <th className="w-16 whitespace-nowrap px-4 py-2 text-left font-semibold text-sm">
                                                  #
                                                </th>
                                                <th className="px-4 py-2 text-left font-semibold text-sm">
                                                  {t("approval.argument")}
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border">
                                              {value.map((argument, index) => (
                                                <tr key={index} className="border-border">
                                                  <td className="px-4 py-2 text-sm">
                                                    {index}
                                                  </td>
                                                  <td className="break-all px-4 py-2 font-mono text-sm">
                                                    {typeof argument === "string" || typeof argument === "number"
                                                      ? String(argument)
                                                      : JSON.stringify(argument) ?? "undefined"}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    typeof value === "string" || typeof value === "number" ? (
                                      String(value)
                                    ) : (
                                      <pre className="max-w-full overflow-x-auto rounded-md bg-muted/60 p-2 text-xs text-muted-foreground">
                                        {JSON.stringify(value, null, 2) ?? "undefined"}
                                      </pre>
                                    )
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            );
          })}

          {status === "running" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              {t("approval.executionInProgress", { seconds: elapsed })}
            </div>
          )}
          {status === "failed" && <Alert variant="destructive" className="p-3 text-sm">{t("approval.decisionFailed")}</Alert>}
          {recoveryError && <Alert variant="destructive" className="p-3 text-sm">{t(recoveryError === "expired" ? "approval.expired" : "approval.unavailable")}</Alert>}
          {staleRun && !resolved && <Alert className="p-3 text-sm text-muted-foreground">{t("approval.staleRun")}</Alert>}
          {checkingRun && !resolved && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              {t("approval.checkingRun")}
            </div>
          )}
        </CardContent>

        {canResolve && !staleRun && !checkingRun && status !== "failed" && !recoveryError && (
          <CardFooter className="justify-end gap-2 pt-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleReject}
              disabled={status === "loading" || status === "running"}
            >
              {status === "loading" && pendingDecision === "reject" && <Spinner className="size-3.5" />}
              {t("approval.reject")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={handleApprove}
              disabled={status === "loading" || status === "running"}
            >
              {status === "loading" && pendingDecision === "approve" && <Spinner className="size-3.5" />}
              {t("approval.approve")}
            </Button>
          </CardFooter>
        )}
      </Card>
    );
  }

  return (
    <Confirmation
      state={confirmationState}
      approval={approval}
      className={cn(
        displayStatus === "rejected" ? "border-destructive/50 text-destructive" : undefined,
      )}
    >
      <ConfirmationAccepted>
        <p className="text-sm font-medium text-green-600 dark:text-green-400">
          ✓ {t("approval.approvedRunResumed")}
        </p>
      </ConfirmationAccepted>

      <ConfirmationRejected>
        <p className="text-sm font-medium text-red-600 dark:text-red-400">
          ✕ {t("approval.rejectedRunCancelled")}
        </p>
      </ConfirmationRejected>
    </Confirmation>
  );
}
